'use client'

import { useEffect, useRef, useState } from 'react'
import Script from 'next/script'
import GoogleAnalytics from '@/components/GoogleAnalytics'
import VisitorTracker from '@/components/VisitorTracker'
import { CONSENT_EVENT, type ConsentState, getStoredConsent } from '@/lib/consent'

/**
 * Porte d'entrée unique pour tout ce qui n'est pas strictement nécessaire au
 * site : GTM (donc GA4/Ads via GTM), VisitorTracker et le pixel Wispra.
 *
 * Audit RGPD (24/09/2026), constat C2 : la première réponse HTML contenait
 * déjà l'iframe GTM et le pixel Wispra, et VisitorTracker posait ses cookies
 * avant tout choix. Ce composant ne rend rien tant que
 * `consent.analytics !== true` : rien n'est demandé au navigateur, donc rien
 * ne part vers Google ou Wispra, et aucun cookie n'est posé.
 *
 * Le composant réagit aussi à un refus après acceptation (`CONSENT_EVENT`) :
 * il se démonte alors immédiatement, ce qui arrête VisitorTracker (plus
 * aucun `sendBeacon` vers /api/track). CookieConsent.tsx efface en parallèle
 * les cookies déjà posés — voir `clearAnalyticsCookies`.
 *
 * Ce démontage n'arrête pas tout : GTM reste chargé mais s'arrête lui-même
 * via Google Consent Mode v2 (CookieConsent.tsx pousse `analytics_storage:
 * denied` dans dataLayer). Le pixel Wispra, lui, n'a pas d'équivalent — une
 * fois son script injecté dans le <head> (hors du rendu React), il continue
 * de tourner tant que la page n'est pas rechargée (audit RGPD 24/09/2026,
 * constat G2). On recharge donc la page au passage de accordé → refusé,
 * une fois les cookies déjà effacés : c'est ce qui arrête, de façon fiable,
 * tout script déjà injecté, GTM et Wispra compris.
 *
 * Le repli `<noscript>` historique de GTM n'est volontairement pas repris
 * ici : un composant client ne s'exécute jamais pour un visiteur sans
 * JavaScript, donc il ne peut de toute façon jamais être gouverné par cette
 * bannière (constaté par l'audit). Le retirer revient seulement à ne plus
 * jamais tracer ces visiteurs sans script — c'est l'issue conforme.
 */
export default function AnalyticsGate() {
  const [analyticsGranted, setAnalyticsGranted] = useState(false)
  // Dernière valeur connue, lue en dehors du rendu : sert uniquement à
  // détecter la transition accordé → refusé, pas à décider du rendu.
  const wasGrantedRef = useRef(false)

  useEffect(() => {
    const stored = getStoredConsent()
    wasGrantedRef.current = !!stored?.analytics
    setAnalyticsGranted(!!stored?.analytics)

    const onConsentChange = (event: Event) => {
      const detail = (event as CustomEvent<ConsentState>).detail
      const granted = !!detail?.analytics
      if (wasGrantedRef.current && !granted) {
        // Le retrait vient de se produire : CookieConsent.tsx a déjà appelé
        // clearAnalyticsCookies() avant de notifier CONSENT_EVENT. Il ne
        // reste plus qu'à arrêter les scripts déjà injectés (Wispra, GTM).
        wasGrantedRef.current = granted
        window.location.reload()
        return
      }
      wasGrantedRef.current = granted
      setAnalyticsGranted(granted)
    }
    window.addEventListener(CONSENT_EVENT, onConsentChange)
    return () => window.removeEventListener(CONSENT_EVENT, onConsentChange)
  }, [])

  if (!analyticsGranted) return null

  return (
    <>
      <GoogleAnalytics />
      <VisitorTracker />
      {/* Wispra Pixel V2 — chargé uniquement après consentement (audit C2) */}
      <Script
        id="wispra-v2"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `(function(d,t){var id='__wispra_v2_'+t;if(d.getElementById(id))return;var s=d.createElement('script');s.id=id;s.src='https://api.wispra.fr/tag/'+t+'/v2/script.js';s.async=true;s.onerror=function(){setTimeout(function(){var r=d.createElement('script');r.src=s.src;r.async=true;(d.head||d.body).appendChild(r)},2000)};(d.head||d.getElementsByTagName('head')[0]||d.body||d.documentElement).appendChild(s);})(document,'cbddf037-b943-4abd-b3d4-f8082ed0d34f');`,
        }}
      />
    </>
  )
}
